/**
 * OutreachGuide — the side panel of the outreach report: what each figure is, and how far it
 * can be trusted. Lee asked for it (6 Oct 2026): "explains the data and explains how accurate
 * and reliable 'opens' actually is (on hubspot and here)".
 *
 * It is written from the figures on the page, so its examples are the real ones.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { fmt } from '../../../utils/analyticsFormat';
import { formatRate, MIN_READERS, MIN_CHECK, CHECK_CEILING, estimatesOf } from '../../../utils/outreachModel';

const Section = ({ title, children }) => (
  <section className="outreach-guide__section">
    <h4>{title}</h4>
    {children}
  </section>
);
Section.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };

const OutreachGuide = ({ sel, pictures, check, cube }) => {
  const v = sel.visits;
  const share = (n, d) => (d ? formatRate(n / d) : '—');
  const outlook = (pictures.programs && pictures.programs.outlook) || 0;
  const readers = sel.readers;
  const rec = sel.recipients;
  const est = estimatesOf(cube, sel.filters);
  return (
    <div className="outreach-guide">
      <Section title="The short version">
        <p>
          Two kinds of number are on this page. <strong>HubSpot’s</strong> (delivered, opened, clicked) say what its
          tracking saw. <strong>Ours</strong> (visits, and fetches of the picture in each email) say what reached
          our own site. Neither is “somebody read it”. Read together they bound it.
        </p>
      </Section>

      <Section title="Estimates: what the orange figures are">
        <p>
          <strong>Everything in orange is an estimate, and nothing else is.</strong> The counted figures say what
          was recorded. The estimates say what probably happened, and are meant to be the more useful number even
          though they are the less certain one. The lighter and fainter an orange figure, the less sure it is.
        </p>
        <p>
          <strong>How the estimate of readers is made.</strong> It starts from a count: emails where a person’s own
          mail program loaded a picture. That leaves out the opens HubSpot marked as machines, the “open” HubSpot
          adds when a link is clicked, Apple’s background fetches, and opens by something only calling itself a
          browser. Then it scales that count up for readers with pictures off, using the people who unsubscribed
          with the link inside an email: they certainly had it open, so the share of them with a mail-program open
          recorded, at any time, is the share of readers such an open sees.
          {est && est.ok ? ` At present that is ${est.base.k} of ${est.base.n}, so one counted open stands for about ${Math.round(est.scale.mid)} readers, and plausibly anything from ${Math.round(est.scale.low)} to ${Math.round(est.scale.high)}.` : ''}
        </p>
        <p>
          <strong>Why to read the range and not the middle.</strong> Whether pictures show is mostly a firm’s mail
          policy, so the people it is scaled from are far fewer separate readings than they look. The range is drawn
          as if there were half as many of them. Each build also measures, by firm, how far people in one firm
          agree; with one unsubscriber a firm that comes out close to none. The <em>wider</em> of the two is used
          until the estimate has been tested, because the measurement covers one source of doubt only: it says
          nothing about whether quiet readers behave like unsubscribers, about scanners that call themselves
          Outlook, or about the same sharing of policy in the count of opens itself. Only two numbers are kept from
          it: how many groups, and that one figure. Nothing about any firm is stored or shown.
        </p>
        <p>
          <strong>The checks, and why a pass means little.</strong> The panel makes three: whether people who
          unsubscribed at once differ from those who did so later; the same for the clicks HubSpot kept, which is
          where a scanner would show; and how far the answer moves if “an open” is defined more widely. Their pass
          lines were fixed before any of those figures existed. All of them are weak: they can only catch a large
          difference, so “no large difference found” is not support for the estimate. A failure sets the
          confidence to very low.
        </p>
        <p>
          <strong>At least, on the record.</strong> Beneath the estimate is a count that needs no scaling: emails
          where the person’s own mail program loaded a picture, or where they unsubscribed with the link inside the
          email. It is the least that happened, and the estimate is what probably did. The clicks HubSpot kept sit
          beside it and can be added by ticking a box, because not all of them are people. It says “on the
          record” and not “proven”, since it rests on reading a mail program’s name. The count and the estimate are
          never added together.
        </p>
        <p>
          <strong>It is untested.</strong> No one has yet checked it where the answer is known. The check is a seed
          test: one of these emails sent to twenty or thirty mailboxes we control (Outlook with pictures off and
          on, Gmail, Apple Mail), with a note of who read it and who clicked, and the estimate worked out for those
          alone. The same test would show inbox against junk folder.
        </p>
        <p>
          <strong>What it cannot do.</strong> It applies one rate to every wave and email, so it cannot say which
          did better: for that, use the counted figures in “Does one do better than another?”. It assumes people
          who unsubscribe show pictures about as often as people who read quietly, which these figures cannot
          check. And an estimate is never added into a counted total anywhere on the page.
        </p>
      </Section>

      <Section title="How reliable is “opened”?">
        <p>
          An email cannot report that it was read. What is recorded is that <strong>a picture in it was fetched</strong>.
          HubSpot hides a tiny picture in every email and counts an open when it is fetched; our logo sits in every
          email at one of our own addresses, so we see the same thing from our side.
        </p>
        <p>That makes “opened” wrong in three ways at once:</p>
        <ul>
          <li>
            <strong>It misses people.</strong> Anyone reading with pictures switched off is never counted. Outlook
            hides pictures from a sender it does not know until the reader asks for them, and Outlook is
            {' '}{share(outlook, pictures.totals.program)} of the mail programs we saw. A first email from a stranger is
            exactly the case it hides. A click proves an open, and HubSpot adds those back, but nothing adds back
            the reader who did not click. How many there are is measured: see the next section.
          </li>
          <li>
            <strong>It counts machines.</strong> A firm’s mail security fetches every picture as the email arrives.
            Of {fmt(pictures.total)} fetches we saw, {fmt(pictures.totals.scanner)} ({share(pictures.totals.scanner, pictures.total)}) were
            that. HubSpot filters the ones it recognises; it does not publish which.
          </li>
          <li>
            <strong>It cannot see Apple Mail at all.</strong> Apple fetches every picture for its Mail users in the
            background, read or not ({fmt(pictures.totals.apple)} of the fetches). Those say an address uses Apple
            Mail, and nothing about reading.
          </li>
        </ul>
        <p>
          So HubSpot’s open rate is not a floor and not a ceiling: it is low on one side and padded on the other.
          It is still the best figure for <em>comparing</em> one email with another, because the same faults apply
          to both.
        </p>
      </Section>

      <Section title="Readers with pictures off: what can be known">
        <p>
          This is the gap that matters most for us. A solicitor who reads every word of an email in Outlook, without
          asking it to show the pictures, is <strong>not an open</strong>: not in HubSpot, not in our own count of the
          logo. Nothing was fetched, so nothing was recorded, by anyone.
        </p>
        <p>
          <strong>A click needs no picture.</strong> So a reader with pictures off does show up the moment they
          click through. That gives one question that can be answered: of the visits we know were people (they did
          on our site what only a person does), in how many had the email loaded a picture before the click?
        </p>
        <p>It takes both sides, and neither can answer alone:</p>
        <ul>
          <li><strong>Our records</strong> say which clicks were people. Most clicks are a firm’s mail security, and HubSpot’s own mark for that is crude.</li>
          <li><strong>HubSpot’s events</strong> say whose click it was, and whether that copy of that email had loaded a picture.</li>
        </ul>
        <p>
          The two are joined by the email’s id and the time: a visit begins within a few seconds of its click. Where
          more than one recipient’s click fits and they do not agree, the visit is left out, not guessed.
          {readers ? ` ${fmt(readers.clicksRead)} clicks were read for it on the last build.` : ''}
        </p>
        <p>
          <strong>What is counted.</strong> For each visit by a person: had any open been recorded before the click?
          “Before” is at least two seconds before. An open that appears only at the moment of the click is most
          likely the click itself, because HubSpot counts a click as an open; it is shown apart from an open that
          came later.
        </p>
        <p>
          <strong>A count, and only sometimes a share.</strong> The page always gives the count. It says “about X in
          10 of the readers we can prove” only once {MIN_READERS} visits by people have been matched and the check
          below has passed. On a handful of visits the range is wide, and the page gives it.
        </p>
        <p>
          <strong>“8 of 10” is not “80%”.</strong> It is eight visits out of ten. Say it that way when passing it on:
          a percentage sounds like a measurement of everyone who was sent the email, and this is a count of the few
          who clicked through and whom we can prove were people. One more visit either way moves it a long way.
        </p>
        <p>
          <strong>Why so few, and the wider base.</strong> The emails have gone to thousands, but the question can
          only be asked of someone who did a thing that needs no picture, and hardly anyone clicked. So the panel
          also asks it of two larger groups, on HubSpot’s word alone that they were people: every click it kept, and
          everyone who unsubscribed. Someone who unsubscribes by the link in the email had it open; someone who used
          their mail program’s own button may not have, so “no open” there is not “read with pictures off”. The
          thousands who neither clicked nor unsubscribed cannot be asked at all.
        </p>
        <p>
          <strong>This panel does not scale HubSpot’s open count up.</strong> It is tempting to say “the open count
          sees 2 in 10 readers, so every open stands for five people”. That corrects for the readers it missed and
          not for the opens that were nobody: a scanner loading the pictures is counted as an open too
          {readers && readers.bands.machine.browser > 0 ? ` (before ${fmt(readers.bands.machine.browser)} of ${fmt(readers.bands.machine.matched)} machine visits, HubSpot had kept one)` : ''}.
          The open count is short on one side and padded on the other. The estimate in orange corrects for both, and
          is kept apart from these counts: see “Estimates” at the top of this guide.
        </p>
        <p>
          <strong>The check.</strong> The same match is made for the visits known to be machines (at least {MIN_CHECK}).
          A scanner clicks as the email arrives, before anyone could have read it, so a person’s own mail program
          loading a picture before that click should be close to never: at most {formatRate(CHECK_CEILING)}, and
          clearly rarer than for people. If either fails, the count stands and no share is drawn from it.
        </p>
        <p>
          <strong>Why it is for the whole campaign.</strong> The figures are kept as totals by kind of visitor and
          outcome, and not by wave or email. A row for one email would usually be a count of one, and for someone
          who can also see HubSpot that is enough to work out who it was. So this panel does not follow the filters.
        </p>
        <p>
          <strong>What nothing can count:</strong> the reader with pictures off who does not click. They can only be
          estimated from the ones who do.
        </p>
        <p>
          <strong>What is not kept.</strong> To make the match, a visit is set beside a recipient’s events while the
          figures are being built, and then dropped. What is stored is “visits by people with no open before the
          click: 8”: a total. Not which visit, not which email, and not whose.
        </p>
      </Section>

      {rec && (
        <Section title="How long an email stayed open">
          <p>
            HubSpot records how long each open lasted. Of the {fmt(rec.sends.delivered)} emails delivered for this
            choice, {fmt(rec.sends.fateRead)} were kept open for eight seconds or more: long enough to read a short
            email. It is the nearest thing there is to “read”, with the same blind spot as every open: it exists only
            where a picture loaded.
          </p>
          <p>
            An open is also thrown out by HubSpot when it judges it automated. That is why its “opened” is far below
            the raw number of opens: {fmt(rec.sends.openedAny)} emails had an open of some kind, and for
            {' '}{fmt(rec.sends.machineOnly)} of them every one was a machine’s.
          </p>
        </Section>
      )}

      <Section title="The estimate that did not work">
        <p>
          Before each recipient’s events were read, we tried to answer the pictures-off question from our own records
          alone. A visit to the site proves the email was opened; if our picture was fetched just before the visit,
          that reader had pictures on; so the share of visits with a fetch just before should be the share of readers
          with pictures on.
        </p>
        <p>
          It does not work, and the test is on this page. Run on the visits we <em>know</em> are machines it finds a
          fetch before {fmt(check.machine.withFetchBefore)} of {fmt(check.machine.visits)}; chance alone would give
          {' '}{fmt(Math.round(check.machine.expectedByChance))}. For people: {fmt(check.person.withFetchBefore)} of
          {' '}{fmt(check.person.visits)}, against {fmt(Math.round(check.person.expectedByChance))} by chance. Our logo has
          one address for every email, so a fetch can be set beside a visit only by the clock, and pictures are fetched
          in a burst after every send: almost any visit has one just before it. A method that gives a scanner the same
          answer as a person is measuring the burst.
        </p>
        <p>That is why the measure above ties each visit to its own email, and carries the same test.</p>
      </Section>

      <Section title="People, machines, and “cannot tell”">
        <p>
          Most clicks on a link in a business email are not people: mail security opens every link to see where it
          goes. Each visit is put in one of three bands, by the first rule that applies.
        </p>
        <ul>
          <li>
            <strong>A person, on the evidence</strong> ({fmt(v.person.n)}): did something a link scanner does not.
            Accepted cookies, opened a second page, used the slider, clicked a button, played the video or started
            a quote.
          </li>
          <li>
            <strong>A machine</strong> ({fmt(v.machine.n)}): a browser run by a program; a cloud or security
            provider’s network; the email’s other link opened within ten seconds (a scanner opens both); or outside
            the UK.
          </li>
          <li>
            <strong>Cannot tell</strong> ({fmt(v.unsure.n)}): no sign of a machine, and nothing only a person does.
            One page, from a UK network.
          </li>
        </ul>
        <p>
          <strong>“Cannot tell” does not mean “probably not a person”.</strong> A solicitor who glances at one page
          and leaves looks exactly like this. So do some scanners. The true number of people lies between
          {' '}{fmt(v.person.n)} and {fmt(v.person.n + v.unsure.n)}, and the page always shows both.
        </p>
        <p>
          Two things are deliberately <em>not</em> evidence. <strong>Time on the page</strong>: the visits known to be
          machines cluster at 19 to 25 seconds, so a scanner can “read” for longer than a person. <strong>Arriving
          in the first minutes</strong>: most of the people on evidence arrived within ten minutes of the send too.
        </p>
        <p>
          Visits that carry the campaign’s tag but no email id are test emails and previews
          {cube.noEmailId && cube.noEmailId.visits ? ` (${fmt(cube.noEmailId.visits)} of them)` : ''}. They are left out.
        </p>
      </Section>

      <Section title="Comparing groups of different sizes">
        <p>
          Wave 1 went to about 1,000 people and wave 5 to about 1,500, so wave 5 will have more of everything.
          Every comparison here is therefore a <strong>rate</strong>, with the count it rests on beside it.
        </p>
        <p>
          The pale band through each dot is the range that rate could lie in by chance alone. Three opens from
          twenty emails is “15%”, but could as easily be 5% or 36%. Two groups differ only where their bands do not
          overlap; the sentence under each chart says whether any does.
        </p>
        <p>
          The waves are also at different ages. A hollow dot is an email under a week old that is still collecting
          opens. To compare like with like, choose “Email 1”.
        </p>
      </Section>

      <Section title="What cannot be split">
        <p>
          Email 1 is sent to heads and to others as two separate emails. Emails 2 and 3 go to both together, and one
          email went to two waves at once. Nothing is shared out by guesswork: what cannot be divided is left out,
          and the page says how many.
        </p>
        {rec ? (
          <p>
            <strong>Opens and clicks are divided</strong>, because each recipient’s own events were read and every
            contact has a wave and a role. <strong>Visits are not</strong>: a visit carries the email’s id and nothing
            about its visitor, so with “Heads” or “Others” chosen the visits from emails 2 and 3 are left out, and
            with one of two waves chosen, the visits from the email they shared.
          </p>
        ) : (
          <p>
            Each recipient’s own events were not read on the last build, so HubSpot’s total for each email is used.
            With “Heads” or “Others” chosen, those totals and the visits from emails 2 and 3 cannot be divided, and are
            left out. The “people” figures come from HubSpot’s record of each contact, so they split by role for every
            email, but not by which email.
          </p>
        )}
      </Section>

      <Section title="What is kept, and what is not">
        <p>
          This page holds <strong>counts only</strong>. No name, email address or contact record is stored in Tools
          or shown here; a count of 1 is one person, but nothing on this page leads from the count to who it was.
          Recipients’ addresses are used while the figures are built, to put an event in its wave and role and to
          match a visit to its email, and are dropped before anything is stored.
          Coming back is recognised only for visitors who accepted cookies, so it is a floor.
        </p>
        <p>
          The figures are rebuilt each night from HubSpot (read-only) and our own records.
          {cube.calls ? ` The last build made ${fmt(cube.calls.used)} requests to HubSpot, against a ceiling of ${fmt(cube.calls.ceiling)}.` : ''}
        </p>
      </Section>
    </div>
  );
};

OutreachGuide.propTypes = {
  sel: PropTypes.object.isRequired,
  pictures: PropTypes.object.isRequired,
  check: PropTypes.object.isRequired,
  cube: PropTypes.object.isRequired,
};

export default OutreachGuide;
